import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, isWeekend, toSegments, type PlanItem, type PlanLoad } from '@jisy/shared';
import { useSectionColors } from '../api/queries.js';
import { localeTag } from '../i18n/index.js';
import { ParentPrefix, SectionMark, sectionColorVar, StatusBadge } from './Badges.js';
import { StatusCell } from './IssueCellEdit.js';
import styles from './PlanGrid.module.css';

/**
 * Сетка планирования.
 *
 * Готовый гантт-компонент здесь не годится: у отрезка обязаны быть разрывы — задача может идти
 * пн–вт, пропускать среду и снова идти чт–пт. Поэтому сетка нарисована ячейками, а отрезок —
 * это последовательность отмеченных дней, которая схлопывается в полосу только визуально.
 *
 * Ввод: протягивание мышью, клавиатура (стрелки, Пробел, Shift+стрелки, Backspace). Первое
 * нажатие задаёт направление — если день был отмечен, протягивание снимает отметки, иначе ставит.
 *
 * Процент выполнения из сетки убран: в строке о ходе работы говорит статус задачи. Хранение
 * и запись процента остались на сервере — возврат колонки не потребует ни миграции, ни маршрута.
 */

/** Выполненная строка: категория статуса из Jira, а не название — названия у каждого сайта свои. */
export function isPlanItemDone(item: PlanItem): boolean {
  return item.issue?.status?.category === 'done';
}

interface DragState {
  itemId: string;
  fromIndex: number;
  toIndex: number;
  /** true — протягивание ставит отметки, false — снимает. */
  paint: boolean;
}

export function PlanGrid({
  items,
  days,
  load,
  editable,
  today,
  onChangeDays,
  onOpenIssue,
  onResolveConflict,
  onRemove,
  onReorder,
  reorderable,
}: {
  items: PlanItem[];
  days: string[];
  load: PlanLoad;
  editable: boolean;
  today: string;
  onChangeDays: (itemId: string, changes: Array<{ day: string; marked: boolean }>) => void;
  onOpenIssue: (issueKey: string) => void;
  onResolveConflict: (itemId: string, resolution: 'local' | 'remote') => void;
  onRemove: (itemId: string) => void;
  onReorder: (itemId: string, beforeItemId: string | null) => void;
  /**
   * Можно ли переносить строки. Отключается, когда порядок задаёт сортировка: перенос считает
   * позиции по видимому порядку, и запись отправила бы в базу расстановку, которой не видно.
   */
  reorderable: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const sectionColor = useSectionColors();
  const [drag, setDrag] = useState<DragState | null>(null);
  const [focused, setFocused] = useState<{ itemId: string; index: number } | null>(null);
  // Перенос строк: какая строка «в руке» и над какой она сейчас находится.
  const [dragRow, setDragRow] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const maxLoad = Math.max(1, ...load.map((entry) => entry.load));

  /** Отметки с учётом текущего протягивания: сетка перерисовывается до отпускания кнопки. */
  const marksOf = useCallback(
    (item: PlanItem): Set<string> => {
      const marks = new Set(item.days);
      if (!drag || drag.itemId !== item.id) return marks;

      const [from, to] =
        drag.fromIndex <= drag.toIndex
          ? [drag.fromIndex, drag.toIndex]
          : [drag.toIndex, drag.fromIndex];

      for (let index = from; index <= to; index += 1) {
        const day = days[index];
        if (!day) continue;
        if (drag.paint) marks.add(day);
        else marks.delete(day);
      }
      return marks;
    },
    [drag, days],
  );

  // Кнопку мыши могли отпустить где угодно, в том числе вне сетки, — слушаем окно.
  useEffect(() => {
    if (!drag) return;

    const finish = (): void => {
      const [from, to] =
        drag.fromIndex <= drag.toIndex
          ? [drag.fromIndex, drag.toIndex]
          : [drag.toIndex, drag.fromIndex];

      const changes: Array<{ day: string; marked: boolean }> = [];
      for (let index = from; index <= to; index += 1) {
        const day = days[index];
        if (day) changes.push({ day, marked: drag.paint });
      }

      setDrag(null);
      if (changes.length > 0) onChangeDays(drag.itemId, changes);
    };

    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => {
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };
  }, [drag, days, onChangeDays]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, item: PlanItem, index: number) => {
      if (!editable) return;
      const marks = new Set(item.days);
      const day = days[index];
      if (!day) return;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const next = index + (event.key === 'ArrowRight' ? 1 : -1);
        if (next < 0 || next >= days.length) return;
        event.preventDefault();

        if (event.shiftKey) {
          // Shift+стрелка протягивает: соседний день получает состояние текущего.
          const target = days[next];
          if (target) onChangeDays(item.id, [{ day: target, marked: marks.has(day) }]);
        }

        setFocused({ itemId: item.id, index: next });
        focusCell(gridRef.current, item.id, next);
        return;
      }

      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onChangeDays(item.id, [{ day, marked: !marks.has(day) }]);
        return;
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        onChangeDays(item.id, [{ day, marked: false }]);
      }
    },
    [days, editable, onChangeDays],
  );

  const gridStyle = useMemo(
    () => ({ '--day-count': days.length }) as React.CSSProperties,
    [days.length],
  );

  return (
    <div className={styles.wrap} ref={gridRef}>
      <div className={styles.head} style={gridStyle}>
        <div className={styles.headMeta}>
          <span className="label">{t('plan.load')}</span>
          <span className={styles.headHint}>{t('plan.loadHint')}</span>
        </div>

        {days.map((day, index) => {
          const value = load[index]?.load ?? 0;
          const weekend = isWeekend(day);
          const isToday = day === today;

          return (
            <div
              key={day}
              className={`${styles.headDay} ${weekend ? styles.weekend : ''} ${isToday ? styles.todayColumn : ''}`}
            >
              {isToday && <span className={styles.todayBar} aria-hidden="true" />}
              <div className={styles.loadRow}>
                <span className={`mono ${styles.loadValue} ${value === 0 ? styles.loadZero : ''}`}>
                  {value}
                </span>
                <span
                  className={styles.loadBar}
                  style={{
                    height: `${Math.round(6 + (value / maxLoad) * 20)}px`,
                    background:
                      value === 0
                        ? 'var(--color-line-2)'
                        : value >= maxLoad
                          ? 'var(--color-accent)'
                          : 'var(--color-load-mid)',
                  }}
                  aria-hidden="true"
                />
              </div>
              <div className={styles.dayLabels}>
                <span className={`${styles.weekday} ${weekend ? styles.weekendText : ''}`}>
                  {weekdayLabel(day)}
                </span>
                <span className={`mono ${styles.dayNumber} ${isToday ? styles.todayText : ''}`}>
                  {dayLabel(day)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.body}>
        {items.map((item) => {
          const marks = marksOf(item);
          const segments = toSegments([...marks].sort());
          const colorIndex = sectionColor(projectKeyOf(item));

          return (
            <div
              className={`${styles.row} ${dropTarget === item.id ? styles.rowDropTarget : ''} ${
                dragRow === item.id ? styles.rowDragging : ''
              } ${isPlanItemDone(item) ? styles.rowDone : ''}`}
              key={item.id}
              // Цвет отрезков строки — цвет её раздела; до загрузки разделов это цвет действия.
              style={
                {
                  ...gridStyle,
                  '--mark-color': sectionColorVar(colorIndex) ?? 'var(--color-accent)',
                } as React.CSSProperties
              }
              onDragOver={(event) => {
                if (!reorderable || !dragRow || dragRow === item.id) return;
                // Без preventDefault браузер не считает строку допустимой целью.
                event.preventDefault();
                setDropTarget(item.id);
              }}
              onDragLeave={() => {
                if (dropTarget === item.id) setDropTarget(null);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragRow && dragRow !== item.id) onReorder(dragRow, item.id);
                setDragRow(null);
                setDropTarget(null);
              }}
            >
              <div className={styles.section}>
                {/*
                  Ручка переноса остаётся в разметке и при сортировке, только скрытой: убери её
                  совсем — метка раздела и название прыгали бы влево на каждом переключении.
                */}
                {editable && (
                  <span
                    className={`${styles.dragHandle} ${reorderable ? '' : styles.dragHandleIdle}`}
                    {...(reorderable
                      ? {
                          draggable: true,
                          onDragStart: () => setDragRow(item.id),
                          onDragEnd: () => {
                            setDragRow(null);
                            setDropTarget(null);
                          },
                          title: t('plan.reorder'),
                        }
                      : {})}
                    aria-hidden="true"
                  >
                    ⣿
                  </span>
                )}
                <SectionMark colorIndex={colorIndex} />
                <span className={`${styles.sectionName} truncate`}>
                  {item.issue?.projectName ?? ''}
                </span>
              </div>

              <button
                type="button"
                className={styles.title}
                onClick={() => onOpenIssue(item.issueKey)}
                title={planTitle(item)}
              >
                <span className={`mono ${styles.titleKey}`}>{item.issueKey}</span>
                <span className={`${styles.titleText} truncate`}>
                  {/* Подзадача в плане стоит без своей задачи — её ключ подсказывает, откуда она. */}
                  {item.issue?.type?.subtask && item.issue.parent && (
                    <ParentPrefix parent={item.issue.parent} />
                  )}
                  {item.issue?.summary ?? ''}
                </span>
                {item.conflicts.length > 0 && (
                  <span className={styles.conflictMark} title={t('plan.conflictTitle')}>
                    !
                  </span>
                )}
              </button>

              <div className={styles.statusCell}>
                {/*
                  Статус правится прямо в строке — тем же списком переходов, что и в таблице.
                  Права на это даёт Jira, а не план: чужой план правит только его владелец,
                  но статус задачи меняет тот, кому Jira это позволяет.
                */}
                {item.issue ? (
                  <StatusCell issue={item.issue} />
                ) : (
                  <StatusBadge status={null} />
                )}
              </div>

              <div className={styles.rowActions}>
                {editable && (
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => onRemove(item.id)}
                    title={t('plan.remove', { key: item.issueKey })}
                    aria-label={t('plan.remove', { key: item.issueKey })}
                  >
                    ✕
                  </button>
                )}
              </div>

              {days.map((day, index) => {
                const marked = marks.has(day);
                const previous = index > 0 && marks.has(days[index - 1] ?? '');
                const next = index < days.length - 1 && marks.has(days[index + 1] ?? '');
                const inGap = !marked && hasMarkBefore(marks, days, index) && hasMarkAfter(marks, days, index);
                const dragging = drag?.itemId === item.id && withinDrag(drag, index);
                const segment = segments.find((entry) => entry.start === day);

                return (
                  <div
                    key={day}
                    data-cell={`${item.id}:${index}`}
                    className={`${styles.cell} ${day === today ? styles.todayColumn : ''}`}
                    tabIndex={editable ? 0 : -1}
                    role="gridcell"
                    aria-selected={marked}
                    aria-label={cellLabel(item, day, marked, t)}
                    onPointerDown={(event) => {
                      if (!editable) return;
                      event.preventDefault();
                      setDrag({ itemId: item.id, fromIndex: index, toIndex: index, paint: !marked });
                      setFocused({ itemId: item.id, index });
                    }}
                    onPointerEnter={() => {
                      if (drag && drag.itemId === item.id) {
                        setDrag({ ...drag, toIndex: index });
                      }
                    }}
                    onFocus={() => setFocused({ itemId: item.id, index })}
                    onKeyDown={(event) => handleKeyDown(event, item, index)}
                  >
                    {inGap && <span className={styles.gap} aria-hidden="true" />}

                    {marked && (
                      <span
                        className={`${styles.mark} ${dragging ? styles.markDragging : ''}`}
                        style={{
                          left: previous ? '-1px' : '4px',
                          right: next ? '-1px' : '4px',
                          borderRadius: `${previous ? 0 : 5}px ${next ? 0 : 5}px ${next ? 0 : 5}px ${previous ? 0 : 5}px`,
                        }}
                      >
                        {segment && (
                          <span className={`mono ${styles.markLabel}`}>
                            {t('plan.days', { count: segment.length })}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {items
          .filter((item) => item.conflicts.length > 0)
          .map((item) => (
            <div className={styles.conflictCard} key={`conflict-${item.id}`} role="alert">
              <div className={styles.conflictText}>
                <span className={styles.conflictTitle}>
                  {t('plan.conflictTitle', { key: item.issueKey })}
                </span>
                <span className={styles.conflictBody}>{t('plan.conflictBody')}</span>
                <div className={styles.conflictDiff}>
                  {item.conflicts.map((conflict) => (
                    <div className={styles.conflictRow} key={conflict.id}>
                      <span className={styles.conflictField}>
                        {t(`plan.conflictField.${conflict.field}`)}
                      </span>
                      <span className={`mono ${styles.conflictMine}`}>
                        {formatConflictValue(conflict.mine)}
                      </span>
                      <span className={styles.conflictArrow} aria-hidden="true">
                        →
                      </span>
                      <span className={`mono ${styles.conflictTheirs}`}>
                        {formatConflictValue(conflict.theirs)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.conflictActions}>
                <button
                  type="button"
                  className={styles.conflictPrimary}
                  onClick={() => onResolveConflict(item.id, 'local')}
                  disabled={!editable}
                >
                  {t('plan.conflictKeepMine')}
                </button>
                <button
                  type="button"
                  className={styles.conflictSecondary}
                  onClick={() => onResolveConflict(item.id, 'remote')}
                  disabled={!editable}
                >
                  {t('plan.conflictTakeTheirs')}
                </button>
              </div>
            </div>
          ))}
      </div>

      <span className="visually-hidden" aria-live="polite">
        {focused
          ? describeRow(items.find((item) => item.id === focused.itemId), t)
          : ''}
      </span>
    </div>
  );
}

function withinDrag(drag: DragState, index: number): boolean {
  const [from, to] =
    drag.fromIndex <= drag.toIndex ? [drag.fromIndex, drag.toIndex] : [drag.toIndex, drag.fromIndex];
  return index >= from && index <= to;
}

function hasMarkBefore(marks: Set<string>, days: string[], index: number): boolean {
  for (let i = 0; i < index; i += 1) if (marks.has(days[i] ?? '')) return true;
  return false;
}

function hasMarkAfter(marks: Set<string>, days: string[], index: number): boolean {
  for (let i = index + 1; i < days.length; i += 1) if (marks.has(days[i] ?? '')) return true;
  return false;
}

function focusCell(root: HTMLElement | null, itemId: string, index: number): void {
  root?.querySelector<HTMLElement>(`[data-cell="${itemId}:${index}"]`)?.focus();
}

function weekdayLabel(day: string): string {
  return new Intl.DateTimeFormat(localeTag(), { weekday: 'short' }).format(new Date(`${day}T00:00:00`));
}

function dayLabel(day: string): string {
  return new Intl.DateTimeFormat(localeTag(), { day: '2-digit', month: '2-digit' }).format(
    new Date(`${day}T00:00:00`),
  );
}

/** Ячейка озвучивается целиком: ключ задачи, дата и состояние. */
function cellLabel(
  item: PlanItem,
  day: string,
  marked: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return `${item.issueKey}, ${dayLabel(day)}, ${marked ? t('plan.marked') : t('plan.unmarked')}`;
}

/** Строка целиком — для скринридера: «WEB-214, отмечено 11–12 и 14–15 августа». */
function describeRow(
  item: PlanItem | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!item) return '';
  const segments = toSegments(item.days);
  const ranges = segments
    .map((segment) =>
      segment.start === segment.end
        ? dayLabel(segment.start)
        : `${dayLabel(segment.start)}–${dayLabel(segment.end)}`,
    )
    .join(', ');

  return t('plan.rowSummary', {
    key: item.issueKey,
    ranges: ranges || t('plan.noDays'),
  });
}

/** Пустое значение в расхождении означает «дата снята», и это надо показать словом. */
function formatConflictValue(value: unknown): string {
  if (typeof value === 'string' && value.length > 0) return dayLabel(value);
  return '—';
}

/**
 * Раздел строки. Задача может быть ещё не в кэше — тогда раздел берётся из ключа задачи:
 * он всегда «<ключ раздела>-<номер>», и метка красится верно с первой отрисовки.
 */
function projectKeyOf(item: PlanItem): string {
  return item.issue?.projectKey ?? item.issueKey.slice(0, item.issueKey.lastIndexOf('-'));
}

export { addDays };

/** Подсказка строки плана: у подзадачи в ней и родитель — одного ключа мало. */
function planTitle(item: PlanItem): string {
  const issue = item.issue;
  if (!issue) return item.issueKey;
  if (issue.type?.subtask && issue.parent) {
    return `${issue.parent.key} ${issue.parent.summary} / ${issue.summary}`;
  }
  return issue.summary;
}
